import React, { useState, useCallback } from 'react';
import { Plus, FileJson } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { PropertyEditor, type PropertyConfig, type PropertyType } from './PropertyEditor';
import type { JsonSchema, SchemaProperty } from '@/services/schemaApi';

interface SchemaVisualBuilderProps {
  schema: JsonSchema;
  onChange: (schema: JsonSchema) => void;
  disabled?: boolean;
}

export function SchemaVisualBuilder({ schema, onChange, disabled }: SchemaVisualBuilderProps) {
  const [expandedProperties, setExpandedProperties] = useState<Set<string>>(new Set());

  // Convert schema properties to PropertyConfig array
  const getPropertyConfigs = useCallback((): PropertyConfig[] => {
    if (!schema.properties) return [];

    return Object.entries(schema.properties).map(([name, prop]) => ({
      name,
      type: prop.type as PropertyType,
      description: prop.description,
      required: schema.required?.includes(name) || false,
      enum: prop.enum,
      pattern: prop.pattern,
      minimum: prop.minimum,
      maximum: prop.maximum,
      items: prop.items,
      properties: prop.properties,
      nestedRequired: prop.required,
    }));
  }, [schema]);

  const propertyConfigs = getPropertyConfigs();

  // Convert PropertyConfig back to schema
  const updateSchemaFromConfigs = useCallback(
    (configs: PropertyConfig[]) => {
      const newProperties: Record<string, SchemaProperty> = {};
      const newRequired: string[] = [];

      configs.forEach((config) => {
        if (!config.name.trim()) return;

        const prop: SchemaProperty = {
          type: config.type,
        };

        if (config.description) prop.description = config.description;
        if (config.enum && config.enum.length > 0) prop.enum = config.enum;
        if (config.pattern) prop.pattern = config.pattern;
        if (config.minimum !== undefined) prop.minimum = config.minimum;
        if (config.maximum !== undefined) prop.maximum = config.maximum;
        if (config.items) prop.items = config.items;
        if (config.properties) prop.properties = config.properties;
        if (config.nestedRequired && config.nestedRequired.length > 0) {
          prop.required = config.nestedRequired;
        }

        newProperties[config.name] = prop;
        if (config.required) {
          newRequired.push(config.name);
        }
      });

      onChange({
        ...schema,
        properties: newProperties,
        required: newRequired.length > 0 ? newRequired : undefined,
      });
    },
    [schema, onChange]
  );

  const handlePropertyChange = useCallback(
    (index: number, newConfig: PropertyConfig) => {
      const oldName = propertyConfigs[index]?.name;
      const newConfigs = [...propertyConfigs];
      newConfigs[index] = newConfig;
      updateSchemaFromConfigs(newConfigs);

      // Update expanded state if name changed
      if (oldName !== newConfig.name && expandedProperties.has(oldName)) {
        const newExpanded = new Set(expandedProperties);
        newExpanded.delete(oldName);
        newExpanded.add(newConfig.name);
        setExpandedProperties(newExpanded);
      }
    },
    [propertyConfigs, updateSchemaFromConfigs, expandedProperties]
  );

  const handleDeleteProperty = useCallback(
    (index: number) => {
      const name = propertyConfigs[index]?.name;
      const newConfigs = propertyConfigs.filter((_, i) => i !== index);
      updateSchemaFromConfigs(newConfigs);

      // Remove from expanded
      if (name) {
        const newExpanded = new Set(expandedProperties);
        newExpanded.delete(name);
        setExpandedProperties(newExpanded);
      }
    },
    [propertyConfigs, updateSchemaFromConfigs, expandedProperties]
  );

  const handleAddProperty = useCallback(() => {
    // Generate unique name
    let baseName = 'new_property';
    let counter = 1;
    const existingNames = new Set(propertyConfigs.map((p) => p.name));

    while (existingNames.has(baseName)) {
      baseName = `new_property_${counter}`;
      counter++;
    }

    const newConfig: PropertyConfig = {
      name: baseName,
      type: 'string',
      required: false,
    };

    updateSchemaFromConfigs([...propertyConfigs, newConfig]);

    // Auto-expand the new property
    setExpandedProperties((prev) => new Set([...prev, baseName]));
  }, [propertyConfigs, updateSchemaFromConfigs]);

  const toggleExpanded = useCallback((name: string) => {
    setExpandedProperties((prev) => {
      const next = new Set(prev);
      if (next.has(name)) {
        next.delete(name);
      } else {
        next.add(name);
      }
      return next;
    });
  }, []);

  return (
    <div className="h-full flex flex-col">
      {/* Schema metadata */}
      <div className="p-4 border-b space-y-4">
        <div className="space-y-2">
          <Label>Schema Title</Label>
          <Input
            value={schema.title || ''}
            onChange={(e) => onChange({ ...schema, title: e.target.value || undefined })}
            placeholder="MySchema"
            disabled={disabled}
          />
        </div>
        <div className="space-y-2">
          <Label>Description</Label>
          <Textarea
            value={schema.description || ''}
            onChange={(e) => onChange({ ...schema, description: e.target.value || undefined })}
            placeholder="Describe what this schema is for..."
            rows={2}
            disabled={disabled}
          />
        </div>
      </div>

      {/* Properties header */}
      <div className="flex items-center justify-between px-4 py-3 border-b bg-muted/30">
        <div className="flex items-center gap-2">
          <FileJson className="h-4 w-4 text-muted-foreground" />
          <span className="font-medium">Properties</span>
          <span className="text-sm text-muted-foreground">
            ({propertyConfigs.length})
          </span>
        </div>
        <Button size="sm" onClick={handleAddProperty} disabled={disabled}>
          <Plus className="h-4 w-4 mr-1" />
          Add Property
        </Button>
      </div>

      {/* Properties list */}
      <ScrollArea className="flex-1">
        <div className="p-4 space-y-3">
          {propertyConfigs.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <p>No properties defined yet.</p>
              <p className="text-sm mt-1">Click "Add Property" to get started.</p>
            </div>
          ) : (
            propertyConfigs.map((config, index) => (
              <PropertyEditor
                key={index}
                property={config}
                onChange={(newConfig) => handlePropertyChange(index, newConfig)}
                onDelete={() => handleDeleteProperty(index)}
                isExpanded={expandedProperties.has(config.name)}
                onToggleExpand={() => toggleExpanded(config.name)}
              />
            ))
          )}
        </div>
      </ScrollArea>
    </div>
  );
}

export default SchemaVisualBuilder;
